/**
 * Register metadata config — the SINGLE source of SharePoint field names.
 *
 * `REGISTERS` maps each `RegisterKey` to its list titles (parent + audit
 * sibling), human-facing prefix, and a field map from SharePoint INTERNAL field
 * names → the typed `RegisterRecord` props. The services read field names ONLY
 * from here — no field name is hard-coded in a service.
 *
 * Reuses the list titles + prefixes from `config/sharepoint.ts` (owned by
 * 04-01) so there is one source of list titles and the `LIST_NAMES`
 * Record<key,title> shape that `useGraphClient` iterates is untouched.
 *
 * x0020 encoding: SharePoint encodes spaces in INTERNAL field names as `_x0020_`
 * (memory `feedback_sp_field_x0020_encoding`). The v1.0 QMS register columns all
 * use space-free PascalCase internal names (01-02-incidents-schema.md), so no
 * encoding is needed for the register field maps below. The helper
 * `x0020(displayName)` is provided for any future spaced internal name; encode
 * at the field-map site, never in a service.
 */

import {
  REGISTER_LIST_NAMES,
  AUDIT_LIST_NAMES,
  CLOSURE_LIST_NAMES,
  REGISTER_PREFIXES,
} from './sharepoint';
import type { RegisterKey } from '../types/qms';

/**
 * Encode a SharePoint display/internal name's spaces as `_x0020_`.
 * Use only where a list/field internal name genuinely contains a space.
 */
export function x0020(name: string): string {
  return name.replace(/ /g, '_x0020_');
}

// ---------------------------------------------------------------------------
// Field maps (SP internal field name → record meaning)
// ---------------------------------------------------------------------------

/**
 * The shared parent-list fields EVERY register carries (conventions §3/§6, checked against the
 * live `/columns` in `lib/registerReaders.test.ts`). Values are the SharePoint INTERNAL field
 * names. A column only some registers carry (`ManagingUnit`, `Severity`, the due date,
 * `ClosedOn`) lives in that register's `extraFields`, never here: reading a missing column
 * through `$expand=fields` returns nothing rather than a 400, which is how ISS-223 stayed silent.
 */
export const SHARED_REGISTER_FIELDS = {
  /** SharePoint built-in title. */
  title: 'Title',
  /** Status choice. */
  status: 'Status',
  /** "Applies to Business Unit". */
  businessUnit: 'BusinessUnit',
  /** Closure-hook status. */
  closureStatus: 'ClosureStatus',
  /** Description / body. */
  description: 'Description',
  /** SharePoint system created date (always present). */
  created: 'Created',
} as const;

/** The glass-box audit sibling field names (identical across registers, §5). */
export const AUDIT_FIELDS = {
  title: 'Title',
  parentRef: 'ParentRef',
  actor: 'Actor',
  entryTimestamp: 'EntryTimestamp',
  entryType: 'EntryType',
  fieldChanged: 'FieldChanged',
  oldValue: 'OldValue',
  newValue: 'NewValue',
  note: 'Note',
} as const;

/** Closure Links field names (02-04). */
export const CLOSURE_LINK_FIELDS = {
  sourceRef: 'SourceRef',
  relationship: 'Relationship',
  targetType: 'TargetType',
  targetRef: 'TargetRef',
  linkStatus: 'LinkStatus',
  notes: 'Notes',
} as const;

/** Care Plan Revisions field names (02-04 — status read is `ApprovalStatus`). */
export const CARE_PLAN_REVISION_FIELDS = {
  title: 'Title',
  approvalStatus: 'ApprovalStatus',
  created: 'Created',
} as const;

// ---------------------------------------------------------------------------
// REGISTERS — the per-register metadata map (the single source)
// ---------------------------------------------------------------------------

/**
 * Register-specific field names (SP internal names). A key is present ONLY when that register's
 * list has the column; the readers treat an absent key as "this register has no such field".
 */
export interface RegisterExtraFields {
  /** The category choice (`Category`, or `RiskCategory` on Risk). Every register has one. */
  category: string;
  /** "Managing Business Unit" — every register but Risk. */
  managingUnit?: string;
  /**
   * The register's own due date: Incidents `ResolveBy`, Complaints `TargetResolutionOn`, CI
   * `CompleteBy`, Risk `ReviewDate`. Feedback has none (C4 ages it on `ReceivedOn`, 30 days).
   */
  dueOn?: string;
  /** `ClosedOn` — Incidents, Complaints, Feedback, CI. Risk has none (FIELDSET §1.8). */
  closedOn?: string;
  /** `Severity` — Incidents only. Read-only here: no lodge sends it (D-13-16-1). */
  severity?: string;
  /** Incidents: `ReportabilityDecision`, the decision every reportability reader keys on. */
  reportabilityDecision?: string;
  /** Incidents: `ReportedByEmail`, the reporter's UPN (the own-only key, ISS-018). */
  reportedByEmail?: string;
  occurredOn?: string;
  reportedOn?: string;
  /** Incidents: the frozen Person column `ReportedBy` (reads null through Graph; display only). */
  reportedBy?: string;
  recipientName?: string;
  recipientStream?: string;
  /** CI / Risk: the polymorphic back-reference. */
  originatingItems?: string;
  /** Risk: `InherentRiskRating`. */
  inherentRating?: string;
  /** Risk: `ResidualRiskRating`. */
  residualRating?: string;
}

/** Per-register metadata: list titles, prefix, and the field map. */
export interface RegisterConfig {
  /** Config key. */
  key: RegisterKey;
  /** SharePoint parent list display title (from sharepoint.ts). */
  listTitle: string;
  /** SharePoint glass-box audit sibling list title (from sharepoint.ts). */
  auditListTitle: string;
  /** Human-facing reference prefix, e.g. `INC` (compose `<PREFIX>-<Id>`). */
  prefix: string;
  /**
   * The `useGraphClient` list-id key for the parent list (the key under which
   * its resolved id appears in `listIds`). Reuses the `LIST_NAMES` shape.
   */
  listIdKey: string;
  /** The `useGraphClient` list-id key for the audit sibling list. */
  auditListIdKey: string;
  /** Shared parent field map (SP internal name → record meaning). */
  fields: typeof SHARED_REGISTER_FIELDS;
  /**
   * Register-specific extra field names (SP internal names). Only keys that
   * apply to this register are present.
   */
  extraFields: RegisterExtraFields;
}

/**
 * `satisfies` (not a type annotation) keeps each register's own key set visible to callers, so
 * `REGISTERS.incidents.extraFields.severity` is a `string` while a generic
 * `REGISTERS[key].extraFields` read must go through `RegisterExtraFields` (optional keys).
 */
export const REGISTERS = {
  incidents: {
    key: 'incidents',
    listTitle: REGISTER_LIST_NAMES.incidents,
    auditListTitle: AUDIT_LIST_NAMES.incidentsAudit,
    prefix: REGISTER_PREFIXES.incidents,
    listIdKey: 'incidents',
    auditListIdKey: 'incidentsAudit',
    fields: SHARED_REGISTER_FIELDS,
    extraFields: {
      category: 'Category',
      managingUnit: 'ManagingUnit',
      dueOn: 'ResolveBy',
      closedOn: 'ClosedOn',
      severity: 'Severity',
      reportabilityDecision: 'ReportabilityDecision',
      reportedByEmail: 'ReportedByEmail',
      occurredOn: 'OccurredOn',
      reportedOn: 'ReportedOn',
      reportedBy: 'ReportedBy',
      recipientName: 'RecipientName',
      recipientStream: 'RecipientStream',
    },
  },
  complaints: {
    key: 'complaints',
    listTitle: REGISTER_LIST_NAMES.complaints,
    auditListTitle: AUDIT_LIST_NAMES.complaintsAudit,
    prefix: REGISTER_PREFIXES.complaints,
    listIdKey: 'complaints',
    auditListIdKey: 'complaintsAudit',
    fields: SHARED_REGISTER_FIELDS,
    extraFields: {
      category: 'Category',
      managingUnit: 'ManagingUnit',
      dueOn: 'TargetResolutionOn',
      closedOn: 'ClosedOn',
    },
  },
  ci: {
    key: 'ci',
    listTitle: REGISTER_LIST_NAMES.continuousImprovement,
    auditListTitle: AUDIT_LIST_NAMES.continuousImprovementAudit,
    prefix: REGISTER_PREFIXES.continuousImprovement,
    listIdKey: 'continuousImprovement',
    auditListIdKey: 'continuousImprovementAudit',
    fields: SHARED_REGISTER_FIELDS,
    extraFields: {
      category: 'Category',
      managingUnit: 'ManagingUnit',
      dueOn: 'CompleteBy',
      closedOn: 'ClosedOn',
      originatingItems: 'OriginatingItems',
    },
  },
  feedback: {
    key: 'feedback',
    listTitle: REGISTER_LIST_NAMES.feedback,
    auditListTitle: AUDIT_LIST_NAMES.feedbackAudit,
    prefix: REGISTER_PREFIXES.feedback,
    listIdKey: 'feedback',
    auditListIdKey: 'feedbackAudit',
    fields: SHARED_REGISTER_FIELDS,
    // No `dueOn`: Feedback has no due-date column by design (C4 ages it on `ReceivedOn`).
    extraFields: {
      category: 'Category',
      managingUnit: 'ManagingUnit',
      closedOn: 'ClosedOn',
    },
  },
  risk: {
    key: 'risk',
    listTitle: REGISTER_LIST_NAMES.risk,
    auditListTitle: AUDIT_LIST_NAMES.riskAudit,
    prefix: REGISTER_PREFIXES.risk,
    listIdKey: 'risk',
    auditListIdKey: 'riskAudit',
    fields: SHARED_REGISTER_FIELDS,
    // No `managingUnit`, `closedOn` or `severity`: the Risk Register has none of those columns.
    extraFields: {
      category: 'RiskCategory',
      dueOn: 'ReviewDate',
      inherentRating: 'InherentRiskRating',
      residualRating: 'ResidualRiskRating',
      originatingItems: 'OriginatingItems',
    },
  },
} satisfies Record<RegisterKey, RegisterConfig>;

/**
 * Closure-loop list metadata (Phase 2). Titles reuse sharepoint.ts; the
 * `Care Plan Revisions` prefix is `CPR`, `Closure Links` is `LNK`.
 */
export const CLOSURE_CONFIG = {
  closureLinks: {
    listTitle: CLOSURE_LIST_NAMES.closureLinks,
    listIdKey: 'closureLinks',
    prefix: 'LNK',
    fields: CLOSURE_LINK_FIELDS,
  },
  carePlanRevisions: {
    listTitle: CLOSURE_LIST_NAMES.carePlanRevisions,
    listIdKey: 'carePlanRevisions',
    prefix: 'CPR',
    fields: CARE_PLAN_REVISION_FIELDS,
  },
} as const;

/**
 * Map a `ClosureTargetType` to the REGISTERS key whose list resolves the
 * target's Title + status (02-04 resolution table). `CarePlanRevision` resolves
 * against the CPR anchor (handled separately in closureService).
 */
export const TARGET_TYPE_TO_REGISTER_KEY: Record<string, RegisterKey> = {
  ContinuousImprovement: 'ci',
  Risk: 'risk',
};
